import {createAction} from 'redux-actions';
import {curry} from 'ramda';

import {DispatchAny, BulkBody} from '@pluto/@types';
import {valueToMoment} from '@pluto/daterange-picker';
import {setNotificationAction} from '@pluto/notification';
import {
    getQcWorkQueue,
    getQCJobs,
    getQCJobsExport,
    changeQCJobPriority,
    assignQCToUser,
    unassignQCFromUser,
    getQCJobsSummary,
    getQCWorkQueueSummary,
    setJobStatus,
    setQcJobDueDate,
} from '@pluto/rest';
import {SummaryPromise} from '@pluto/common/summary';

import {
    QCFiltersParams,
    QCJobsPromise,
} from '../QCManagerTypes';

export type IDObj = {
    id: number;
}

export type UnAssignIdObj = {
    id: number;
    userId: number | null;
}

export type PriorityRequestBody = {
    id: number;
    checkedItems: IDObj[];
};

export type QCJobAssignment = {
    id: number;
    userId: number;
}

export type DueDateSetting = {
    id: number;
    dueDate: string;
}

export type AssignRequestBody = QCJobAssignment[];
export type UnassignRequestBody = IDObj[];
export type DueDateSettingRequestBody = DueDateSetting[];

export const QC_JOBS_LIST_SET = 'QC_JOBS_LIST_SET';
export const QC_JOBS_EXPORT = 'QC_JOBS_EXPORT';
export const QC_JOBS_SUMMARY_SET = 'QC_JOBS_SUMMARY_SET';
export const QC_JOBS_LIST_RESET = 'QC_JOBS_LIST_RESET';
export const QC_JOBS_PRIORITY_CHANGE = 'QC_JOBS_PRIORITY_CHANGE';
export const QC_JOBS_ASSIGNEE_CHANGE = 'QC_JOBS_ASSIGNEE_CHANGE';
export const QC_WORK_QUEUE_SET = 'QC_WORK_QUEUE_SET';
export const QC_WORK_QUEUE_RESET = 'QC_WORK_QUEUE_RESET';
export const QC_WORK_QUEUE_SUMMARY_SET = 'QC_WORK_QUEUE_SUMMARY_SET';
export const QC_SUMMARY_UPDATE = 'QC_SUMMARY_UPDATE';

export const dateToUtc = (dateType: string, date?: string) => {
    const initialDateValue = valueToMoment(date || null).utc(true);

    if (!date) {
        return date;
    }

    if (dateType === 'start') {
        return initialDateValue.add(1, 's').format();
    }

    return initialDateValue.add(1, 'd').add(-1, 's').format();
};

export const setQCWorkQueueAction = createAction<QCJobsPromise, QCFiltersParams>(QC_WORK_QUEUE_SET, async (params: QCFiltersParams) => {
    const {
        dateStart,
        dateEnd,
        finishDateStart,
        finishDateEnd,
        ...restParams
    } = params;

    const body = {
        ...restParams,
        qcJobFinishDateFrom: dateToUtc('start', finishDateStart),
        qcJobFinishDateTo: dateToUtc('end', finishDateEnd),
        qcJobDueDateFrom: dateToUtc('start', dateStart),
        qcJobDueDateTo: dateToUtc('end', dateEnd),
    };

    return await getQcWorkQueue(body);
});

export const resetQCWorkQueueAction = createAction(QC_WORK_QUEUE_RESET);

export const setQCJobsListAction = createAction<QCJobsPromise, QCFiltersParams>(QC_JOBS_LIST_SET, async (params: QCFiltersParams) => {
    const {
        dateStart,
        dateEnd,
        finishDateStart,
        finishDateEnd,
        ...restParams
    } = params;
    const body = {
        ...restParams,
        qcJobFinishDateFrom: dateToUtc('start', finishDateStart),
        qcJobFinishDateTo: dateToUtc('end', finishDateEnd),
        qcJobDueDateFrom: dateToUtc('start', dateStart),
        qcJobDueDateTo: dateToUtc('end', dateEnd),
    };

    return await getQCJobs(body);
});

export const exportQCJobsAction = createAction<void, QCFiltersParams>(QC_JOBS_EXPORT, async (params: QCFiltersParams) => {
    return await getQCJobsExport(params);
});

export const getQCWorkQueueSummaryAction = createAction<SummaryPromise>(QC_WORK_QUEUE_SUMMARY_SET, async () => {
    return await getQCWorkQueueSummary();
});

export const getQCJobsSummaryAction = createAction<SummaryPromise>(QC_JOBS_SUMMARY_SET, async () => {
    return await getQCJobsSummary();
});

export const updateQCJobSummary = createAction(QC_SUMMARY_UPDATE, (body: BulkBody[]) => body);

export const resetQCJobsListAction = createAction(QC_JOBS_LIST_RESET);

export const changeStatusQCJobAction = curry(async (body: BulkBody[], params: QCFiltersParams, dispatch: DispatchAny) => {
    const result = await setJobStatus(body);

    if (result.statusText === 'OK') {
        dispatch(updateQCJobSummary(body));
        return dispatch(setQCWorkQueueAction(params));
    }

    return dispatch(setNotificationAction({
        type: 'error',
        text: `Wasn't able to change job(s) status. Please try again`,
    }));
});

export const changeQCJobPriorityAction = curry(async (body: PriorityRequestBody, params: QCFiltersParams, dispatch: DispatchAny) => {
    const result = await changeQCJobPriority(body);

    if (result.statusText === 'OK') {
        return dispatch(setQCJobsListAction(params));
    }

    return dispatch(setNotificationAction({
        type: 'error',
        text: `Wasn't able to change job(s) priority. Please try again`,
    }));
});

export const assignQCJobAction = curry(async (body: AssignRequestBody, params: QCFiltersParams, dispatch: DispatchAny) => {
    const result = await assignQCToUser(body);

    if (result.statusText === 'OK') {
        return dispatch(setQCJobsListAction(params));
    }

    return dispatch(setNotificationAction({
        type: 'error',
        text: `Wasn't able to assign job(s) to user. Please try again`,
    }));
});

export const setQcJobDueDateAction = curry(async (body: DueDateSettingRequestBody, params: QCFiltersParams, dispatch: DispatchAny) => {
    const result = await setQcJobDueDate(body);

    if (result.statusText === 'OK') {
        return dispatch(setQCJobsListAction(params));
    }

    return dispatch(setNotificationAction({
        type: 'error',
        text: `Wasn't able to assign job(s) to user. Please try again`,
    }));
});

export const unassignQCJobAction = curry(async (body: UnassignRequestBody, params: QCFiltersParams, dispatch: DispatchAny) => {
    const result = await unassignQCFromUser(body);

    if (result.statusText === 'OK') {
        return dispatch(setQCJobsListAction(params));
    }

    return dispatch(setNotificationAction({
        type: 'error',
        text: `Wasn't able to unassign job(s) from user. Please try again`,
    }));
});
